import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { isWeekend } from '../utils/answerValidations';

import stations from "../data/stations.json";
import routes from "../data/routes.json";
import shapes from "../data/shapes.json";

import './MapFrame.scss';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const MANHATTAN_TILT = 29;

const MapFrame = (props) => {
  const { trip, solution } = props;
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [lng, setLng] = useState(-73.98119);
  const [lat, setLat] = useState(40.75855);
  const [zoom, setZoom] = useState(12);

  const stopsGeoJson = () => {
    const stops = [
      solution.origin,
      solution.first_transfer_arrival,
      solution.first_transfer_departure,
      solution.second_transfer_arrival,
      solution.second_transfer_departure,
      solution.destination
    ];
    return {
      "type": "FeatureCollection",
      "features": [...new Set(stops)].map((stopId) => {
        const station = stations[stopId];
        return {
          "type": "Feature",
          "properties": {
            "id": stopId,
            "name": station.name,
          },
          "geometry": {
            "type": "Point",
            "coordinates": [station.longitude, station.latitude]
          }
        }
      })
    };
  }

  const lineGeoJson = (line) => {
    const route = routes[line.route];
    let shape;
    const beginCoord = [stations[line.begin].longitude, stations[line.begin].latitude];
    const endCoord = [stations[line.end].longitude, stations[line.end].latitude];
    let coordinates = [];

    if (line.route === 'A') {
      const lineA1 = shapes['A1'];
      if (lineA1.some((coord) => coord[0] === beginCoord[0] && coord[1] === beginCoord[1]) && lineA1.some((coord) => coord[0] === endCoord[0] && coord[1] === endCoord[1])) {
        shape = shapes['A1'];
      } else {
        shape = shapes['A2'];
      }
    } else if (line.route === 'F' && isWeekend) {
      shape = shapes['F-Weekend'];
    } else {
      shape = shapes[line.route];
    }

    const beginIndex = shape.findIndex((coord) => coord[0] === beginCoord[0] && coord[1] === beginCoord[1]);
    const endIndex = shape.findIndex((coord) => coord[0] === endCoord[0] && coord[1] === endCoord[1]);

    if (beginIndex < endIndex) {
      coordinates = shape.slice(beginIndex, endIndex + 1);
    } else {
      coordinates = shape.slice(endIndex, beginIndex + 1);
    }

    return {
      "type": "Feature",
      "properties": {
        "color": route.color,
      },
      "geometry": {
        "type": "LineString",
        "coordinates": coordinates
      }
    }
  }

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/fryvex/cmb36yvy700ac01qvfp3deajd?optimize=true',
      center: [lng, lat],
      bearing: MANHATTAN_TILT,
      minZoom: 9,
      zoom: zoom,
      maxBounds: [
        [-74.8113, 40.1797],
        [-73.3584, 41.1247]
      ],
      maxPitch: 0,
    });
    map.current.dragRotate.disable();
    map.current.touchZoomRotate.disableRotation();

    map.current.on('load', () => {
      map.current.resize();
      let coordinates = [];
      [
        {
          route: trip[0],
          begin: solution.origin,
          end: solution.first_transfer_arrival,
        },
        {
          route: trip[1],
          begin: solution.first_transfer_departure,
          end: solution.second_transfer_arrival,
        },
        {
          route: trip[2],
          begin: solution.second_transfer_departure,
          end: solution.destination,
        },
      ].forEach((line, i) => {
        const lineJson = lineGeoJson(line);
        coordinates = coordinates.concat(lineJson.geometry.coordinates);
        const layerId = `line-${i}`;
        map.current.addSource(layerId, {
          "type": "geojson",
          "data": lineJson
        });
        map.current.addLayer({
          "id": layerId,
          "type": "line",
          "source": layerId,
          "layout": {
            "line-join": "miter",
            "line-cap": "round",
          },
          "paint": {
            "line-width": 2,
            "line-color": ["get", "color"],
          }
        });
      });
      const stopsJson = stopsGeoJson();
      map.current.addSource("Stops", {
        "type": "geojson",
        "data": stopsJson
      });
      map.current.addLayer({
        "id": "Stops",
        "type": "symbol",
        "source": "Stops",
        "layout": {
          "text-field": ['get', 'name'],
          "text-size": 12,
          "text-font": ['Lato Bold', "Open Sans Bold","Arial Unicode MS Bold"],
          "text-optional": false,
          "text-justify": "auto",
          'text-allow-overlap': false,
          "text-padding": 1,
          "text-variable-anchor": ["bottom-right", "top-right", "bottom-left", "top-left", "right", "left", "bottom"],
          "text-radial-offset": 0.5,
          "icon-image": "express-stop",
          "icon-size": 8/13,
          "icon-allow-overlap": true,
        },
        "paint": {
          "text-color": '#ffffff',
        },
      });
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: {
            top: 20,
            right: 20,
            left: 20,
            bottom: 150,
          },
          bearing: MANHATTAN_TILT,
        });
      }
    });
  }, []);

  // Update map sources/layers whenever the trip or solution changes
  useEffect(() => {
    if (!map.current) return;

    const update = () => {
      let coordinates = [];
      [
        {
          route: trip[0],
          begin: solution.origin,
          end: solution.first_transfer_arrival,
        },
        {
          route: trip[1],
          begin: solution.first_transfer_departure,
          end: solution.second_transfer_arrival,
        },
        {
          route: trip[2],
          begin: solution.second_transfer_departure,
          end: solution.destination,
        },
      ].forEach((line, i) => {
        const lineJson = lineGeoJson(line);
        coordinates = coordinates.concat(lineJson.geometry.coordinates);
        const layerId = `line-${i}`;
        if (map.current.getSource(layerId)) {
          map.current.getSource(layerId).setData(lineJson);
        } else {
          map.current.addSource(layerId, {
            "type": "geojson",
            "data": lineJson
          });
          map.current.addLayer({
            "id": layerId,
            "type": "line",
            "source": layerId,
            "layout": {
              "line-join": "miter",
              "line-cap": "round",
            },
            "paint": {
              "line-width": 2,
              "line-color": ["get", "color"],
            }
          });
        }
      });

      const stopsJson = stopsGeoJson();
      if (map.current.getSource("Stops")) {
        map.current.getSource("Stops").setData(stopsJson);
      } else {
        map.current.addSource("Stops", {
          "type": "geojson",
          "data": stopsJson
        });
        map.current.addLayer({
          "id": "Stops",
          "type": "symbol",
          "source": "Stops",
          "layout": {
            "text-field": ['get', 'name'],
            "text-size": 12,
            "text-font": ['Lato Bold', "Open Sans Bold","Arial Unicode MS Bold"],
            "text-optional": false,
            "text-justify": "auto",
            'text-allow-overlap': false,
            "text-padding": 1,
            "text-variable-anchor": ["bottom-right", "top-right", "bottom-left", "top-left", "right", "left", "bottom"],
            "text-radial-offset": 0.5,
            "icon-image": "express-stop",
            "icon-size": 8/13,
            "icon-allow-overlap": true,
          },
          "paint": {
            "text-color": '#ffffff',
          },
        });
      }

      // update bounds to fit new route
      if (coordinates.length) {
        const bounds = coordinates.reduce((bounds, coord) => {
          return bounds.extend(coord);
        }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

        if (!bounds.isEmpty()) {
          map.current.fitBounds(bounds, {
            padding: {
              top: 20,
              right: 20,
              left: 20,
              bottom: 150,
            },
            bearing: MANHATTAN_TILT,
          });
        }
      }
    };

    if (!map.current.isStyleLoaded()) {
      map.current.once('load', update);
    } else {
      update();
    }

  }, [trip, solution]);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize
    map.current.on('move', () => {
      setLng(map.current.getCenter().lng.toFixed(4));
      setLat(map.current.getCenter().lat.toFixed(4));
      setZoom(map.current.getZoom().toFixed(2));
    });
  });

  return (
    <div>
      <div ref={mapContainer} className="map-container" />
    </div>
  );
}

export default MapFrame;
